<script setup lang="ts">
import { ref, watch } from 'vue';
import { UiAside, UiButton, UiText, UiIcon } from '@shopexperts/shared';
import SuccessCheckmarkIcon from '../../../assets/icons/success-chekmark.svg';
import RequestReviewForm from './RequestReviewForm.vue';

const asideOpen = defineModel<boolean>('open');

// Track form vs success state
const showSuccess = ref(false);
const shouldRenderForm = ref(false);

// Watch aside opening to lazy-load the form
watch(asideOpen, (isOpen) => {
  if (isOpen && !shouldRenderForm.value) {
    shouldRenderForm.value = true;
  }
}, { immediate: true });

const onCancel = () => {
  asideOpen.value = false;
  // Reset to form state after closing
  setTimeout(() => {
    showSuccess.value = false;
  }, 300);
};

const onSuccess = () => {
  showSuccess.value = true;
};

const onNewReviewRequest = () => {
  showSuccess.value = false;
  // Form will reset itself
};
</script>

<template>
  <UiAside v-model="asideOpen" position="right" :close-on-overlay-click="false">
    <div class="px-4 py-6 md:p-8 flex flex-col gap-4 h-full overflow-y-auto">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <UiText as="h3" variant="h3">
          <span class="font-normal">Request a </span>
          <span class="font-besley italic">review</span>
        </UiText>
        <UiButton variant="ghost" shape="square" @click="onCancel">
          <template #before_label>
            <UiIcon name="close" class="w-6 h-6" />
          </template>
        </UiButton>
      </div>

      <!-- Description (hidden when showing success) -->
      <UiText 
        v-if="!showSuccess" 
        as="p" 
        variant="p" 
        class="text-se-gray-600"
        text="Send a personalized review request to your past clients - on or off shopexperts platform. Reviews help build trust and boost your profile."
      />

      <!-- Form State -->
      <RequestReviewForm 
        v-if="!showSuccess && shouldRenderForm" 
        @success="onSuccess" 
      />

      <!-- Success State -->
      <div 
        v-if="showSuccess" 
        class="flex flex-col items-center justify-center gap-6 py-12 text-center h-full"
      >
        <!-- Success Icon -->
        <div class="w-20 h-20">
            <SuccessCheckmarkIcon class="w-full h-full" />
        </div>

        <!-- Success Message -->
        <div class="flex flex-col gap-2">
          <UiText as="h4" variant="h4" class="font-semibold text-se-gray-900" text="Review request sent successfully!" />
          <UiText as="p" variant="p" class="text-se-gray-600" text="Your client will get your review request by email and in-app. Once they leave a review, it will improve your profile and help you get noticed by more leads." />
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center gap-3">
          <UiButton 
            variant="primary" 
            label="New Review Request"
            @click="onNewReviewRequest"
          />
          <UiButton 
            variant="outline" 
            label="Close"
            @click="onCancel"
          />
        </div>
      </div>
    </div>
  </UiAside>
</template>

